import { batched, fetchAll } from "@/utils/omsGraphqlBatch";
import { cancellationRowFor, parseInstant } from "@/utils/omsCancellations";
import { Q_CANCELLED_ORDERS, SELECTIONS } from "@/graphql/omsCancellations";

// The selections live with every other cancellations operation in
// src/graphql/omsCancellations.js, where graphql:check validates them.
// INVENTORY_FIELDS carries cancelResolvedAt because resumableOutcome()
// (utils/omsCancellations.js) needs it to tell a part-way resolution's own
// stamp apart from an unrelated earlier one.
const { LINE_FIELDS, MATCH_FIELDS, INVENTORY_FIELDS, DECISION_FIELDS, EVENT_FIELDS } = SELECTIONS;

/** The earliest `cancelled` event's observedAt, or null. The release compares
 * this instant, so the page must too (spec §3.1). */
function earliestCancelledAt(page) {
  let best = null;
  let bestMs = null;
  ((page && page.items) || []).forEach((e) => {
    if (!e || e.toStatus !== "cancelled") return;
    const ms = parseInstant(e.observedAt);
    if (ms !== null && (bestMs === null || ms < bestMs)) {
      best = e.observedAt;
      bestMs = ms;
    }
  });
  return best;
}

export async function fetchCancellationRows(api, owner) {
  const orders = await fetchAll(api, Q_CANCELLED_ORDERS,
    { owner, status: { eq: "cancelled" }, limit: 500 }, "ordersByOwnerAndStatus");

  const lineMap = await batched(api, "BatchLines", "orderLinesByOrder", "orderId", "ID",
    orders.map((o) => o.id), { connection: true, body: `items { ${LINE_FIELDS} } nextToken` });
  const pairs = [];
  orders.forEach((order) => {
    const page = lineMap[order.id];
    ((page && page.items) || []).forEach((line) => { if (line && !line.removed) pairs.push({ order, line }); });
  });
  if (pairs.length === 0) return [];

  const lineIds = pairs.map((p) => p.line.id);
  const orderIds = [...new Set(pairs.map((p) => p.order.id))];
  const [matchMap, decisionMap, eventMap] = await Promise.all([
    batched(api, "BatchMatches", "orderMatchesByLine", "orderLineId", "ID", lineIds,
      { connection: true, body: `items { ${MATCH_FIELDS} } nextToken` }),
    batched(api, "BatchDecisions", "getOrderCancellation", "id", "ID!", lineIds,
      { connection: false, body: DECISION_FIELDS }),
    batched(api, "BatchEvents", "orderStatusEventsByOrder", "orderId", "ID", orderIds,
      { connection: true, body: `items { ${EVENT_FIELDS} } nextToken` }),
  ]);
  pairs.forEach((p) => {
    const page = matchMap[p.line.id];
    p.match = ((page && page.items) || [])[0] || null;
  });

  pairs.forEach((p) => { p.decision = decisionMap[p.line.id] || null; });
  // The item a row shows: the one its decision was made about, else its match.
  const itemIdFor = (p) => {
    if (p.decision && p.decision.inventoryId) return p.decision.inventoryId;
    return p.match && p.match.state === "matched" ? p.match.inventoryId || null : null;
  };
  const invIds = [...new Set(pairs.map(itemIdFor).filter(Boolean))];
  const invMap = invIds.length
    ? await batched(api, "BatchInventory", "getInventory", "id", "ID!", invIds, { connection: false, body: INVENTORY_FIELDS })
    : {};

  return pairs.map((p) => {
    const id = itemIdFor(p);
    return cancellationRowFor(p.order, p.line, p.match, id ? invMap[id] || null : null, p.decision,
      earliestCancelledAt(eventMap[p.order.id]));
  });
}
